import { computed } from '@angular/core';
import { signalStoreFeature, withComputed, withState } from '@ngrx/signals';

/** A discriminated union, so `error` is only reachable in the error state. */
export type RequestStatus =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'fulfilled' }
  | { status: 'error'; error: unknown };

/** The state slice this feature contributes. */
export type RequestStatusState = { requestStatus: RequestStatus };

/**
 * Compose into any HTTP-backed data-access store instead of re-implementing the status:
 *
 * ```ts
 * signalStore(withRequestStatus, withMethods((store) => ({ ... })));
 * // in a method: patchState(store, setPending());
 * ```
 */
export const withRequestStatus = signalStoreFeature(
  withState<RequestStatusState>(() => ({ requestStatus: { status: 'idle' } })),
  withComputed(({ requestStatus }) => ({
    isPending: computed(() => requestStatus().status === 'pending'),
    isFulfilled: computed(() => requestStatus().status === 'fulfilled'),
    error: computed(() => {
      const current = requestStatus();
      return current.status === 'error' ? current.error : null;
    }),
  })),
);

/** `patchState` updater flipping the slice to pending. */
export function setPending(): RequestStatusState {
  return { requestStatus: { status: 'pending' } };
}

/** `patchState` updater flipping the slice to fulfilled. */
export function setFulfilled(): RequestStatusState {
  return { requestStatus: { status: 'fulfilled' } };
}

/** `patchState` updater recording a request failure. */
export function setError(error: unknown): RequestStatusState {
  return { requestStatus: { status: 'error', error } };
}
